import { Calendar, CheckCircle, DollarSign, Eye, Pencil, Percent, Ticket, Trash, Users, XCircle } from 'lucide-react';
import Button from '../../../components/Button';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';
import type { ReferralCodeData } from '../services/referralCodesService';

interface ReferralCodeCardProps {
  code: ReferralCodeData;
  onEdit: (code: ReferralCodeData) => void;
  onDelete: (code: string) => void;
  onViewUsers: (code: string) => void;
}

export const ReferralCodeCard = ({ code, onEdit, onDelete, onViewUsers }: ReferralCodeCardProps) => {
  const formatDate = (date: Date): string => {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isExpired = code.expiresAt ? new Date(code.expiresAt) < new Date() : false;

  return (
    <div className={`border-2 rounded-lg p-4 space-y-3 bg-white shadow-md transition-all ${isExpired ? 'opacity-60' : ''}`}>
      {/* Header with Code and Status */}
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Ticket className="w-5 h-5 text-gray-600 flex-shrink-0" />
          <h3 className="text-xl font-bold text-gray-900 break-all">{code.code}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onEdit(code)}
            variant="ghost"
            size="sm"
            icon={<Pencil className="w-4 h-4" />}
            title="Edit code"
          />
          <ButtonWithConfirmation
            onClick={() => onDelete(code.code)}
            firstIcon={<Trash className="w-4 h-4" />}
            secondIcon={<Trash className="w-4 h-4" />}
            firstVariant="ghost"
            secondVariant="danger"
            size="sm"
            firstText=""
            secondText="Really delete?"
          />
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${code.isActive && !isExpired
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
              }`}
          >
            {code.isActive && !isExpired ? (
              <>
                <CheckCircle className="w-3.5 h-3.5" />
                Active
              </>
            ) : (
              <>
                <XCircle className="w-3.5 h-3.5" />
                {isExpired ? 'Expired' : 'Inactive'}
              </>
            )}
          </span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
          <Users className="w-4 h-4 text-gray-600" />
          <div className="flex items-center justify-between w-full gap-2">
            <div>
              <p className="text-xs text-gray-500">Uses</p>
              <p className="text-lg font-semibold text-gray-900">{code.usesCount}</p>
            </div>

            <Button
              onClick={() => onViewUsers(code.code)}
              variant="ghost"
              size="sm"
              icon={<Eye className="w-4 h-4" />}
              title="View users"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
          <DollarSign className="w-4 h-4 text-purple-600" />
          <div>
            <p className="text-xs text-gray-500">Revenue Share</p>
            <p className="text-lg font-semibold text-purple-800">{code.revenueSharePercent}%</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
          <Percent className="w-4 h-4 text-blue-600" />
          <div>
            <p className="text-xs text-gray-500">Discount</p>
            <p className="text-lg font-semibold text-blue-800">{code.discountPercent}%</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
          <Calendar className="w-4 h-4 text-gray-600" />
          <div>
            <p className="text-xs text-gray-500">Created</p>
            <p className="text-sm font-medium text-gray-900">{formatDate(code.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Expiration Date */}
      <div className="pt-2 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <span className="text-xs text-gray-500">Expires:</span>
          <span className={`text-sm font-medium ${isExpired ? 'text-red-600' : 'text-gray-700'}`}>
            {code.expiresAt ? formatDate(code.expiresAt) : 'No expiration'}
          </span>
        </div>
      </div>

      {/* Apply Result Message */}
      {code.applyResultMessage && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-600 italic">{code.applyResultMessage}</p>
        </div>
      )}
    </div>
  );
};
